import type { CostItem } from '../types';

const CODE_FALLBACK_PREFIX = 'CST';

const normalizeCodeToken = (value: string): string =>
  value.replace(/[^A-Z0-9]/g, '');

const toCodePrefix = (category?: string): string => {
  const token = normalizeCodeToken(String(category || '').toUpperCase());
  return token.slice(0, 3) || CODE_FALLBACK_PREFIX;
};

export const normalizeCostItemCode = (code?: string | null): string => {
  const cleaned = String(code || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

  return cleaned;
};

const generateWithPrefix = (prefix: string, usedCodes: Set<string>): string => {
  let sequence = 1;
  while (sequence < 10000) {
    const candidate = `${prefix}-${String(sequence).padStart(2, '0')}`;
    if (!usedCodes.has(candidate)) {
      return candidate;
    }
    sequence += 1;
  }

  return `${prefix}-${Date.now().toString().slice(-4)}`;
};

export const ensureCostItemCode = (
  item: Pick<CostItem, 'id' | 'category' | 'code'>,
  fallbackIndex = 0
): string => {
  const normalized = normalizeCostItemCode(item.code);
  if (normalized) {
    return normalized;
  }

  const prefix = toCodePrefix(item.category);
  const idToken = normalizeCodeToken(String(item.id || '').toUpperCase()).slice(-2);
  if (idToken) {
    return `${prefix}-${idToken}`;
  }

  return `${prefix}-${String(fallbackIndex + 1).padStart(2, '0')}`;
};

export const generateNextCostItemCode = (
  existingItems: Array<Pick<CostItem, 'id' | 'category' | 'code'>>,
  category?: string
): string => {
  const usedCodes = new Set<string>();
  existingItems.forEach((item, index) => {
    const code = ensureCostItemCode(item, index);
    if (code) {
      usedCodes.add(code);
    }
  });

  const prefix = toCodePrefix(category);
  return generateWithPrefix(prefix, usedCodes);
};

export const assignCostItemCodes = (items: CostItem[]): CostItem[] => {
  const usedCodes = new Set<string>();

  return items.map((item, index) => {
    const normalized = normalizeCostItemCode(item.code);
    if (normalized && !usedCodes.has(normalized)) {
      usedCodes.add(normalized);
      if (normalized === item.code) {
        return item;
      }
      return { ...item, code: normalized };
    }

    const prefix = toCodePrefix(item.category);
    const generated = generateWithPrefix(prefix, usedCodes);
    usedCodes.add(generated);
    return { ...item, code: generated };
  });
};
